import { useState, useEffect, useCallback } from "react";
import { AdminCard, AdminCardHead, LoadFailedBanner } from "components/admin/ui";
import { adminGet, adminPut, adminPostForm } from "lib/adminClient";
import { mediaUrl } from "lib/media";
import SlideOptions from "components/admin/SlideOptions";
import SlideImages from "components/admin/SlideImages";

/**
 * Editor for the honours department pages (/departments/:slug).
 *
 * Built for non-technical staff: each department opens into tabs (Basics,
 * Banner, Programmes, Curriculum, People, Facilities, More) instead of one long
 * scroll; every field is labelled and most carry a hint saying where it shows
 * on the site; courses are edited as rows, not as a delimited text blob; and a
 * save bar stays pinned to the bottom with an unsaved-changes indicator.
 *
 * A department's `code` is what links the chips on the homepage journey card
 * (FNS / CDSR / FD) to its page.
 */

const inputCls =
  "w-full px-3 py-2 border-[1.5px] border-line rounded-lg font-sans text-sm outline-none focus:border-green bg-white";
const btnPrimary =
  "bg-green text-white border-none rounded-lg px-3.5 py-2 font-sans font-semibold text-[12px] cursor-pointer hover:opacity-90 disabled:opacity-50";
const btnGhost =
  "bg-white text-ink border-[1.5px] border-line rounded-lg px-3 py-1.5 font-sans font-semibold text-[12px] cursor-pointer hover:border-green";
const btnDanger = "bg-[#fdeaea] border-none rounded-md px-2.5 py-2 cursor-pointer text-xs text-[#c44]";
const btnIcon = "bg-[#f2f4f6] border-none rounded-md px-2 py-1 cursor-pointer text-xs disabled:opacity-40";

const slugify = (s) =>
  String(s || "")
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const blankDept = () => ({
  slug: "",
  code: "",
  name: "",
  institution: "honors",
  color: "#9B7200",
  kicker: "Honours Department · Khulna University",
  tagline: "",
  slides: [],
  about: "",
  aboutMore: "",
  admissionsStatus: "",
  applyHref: "",
  footnote: "",
  stats: [],
  highlight: {},
  pillars: [],
  programmes: [],
  curriculum: [],
  courses: [],
  head: {},
  faculty: [],
  facilities: [],
  showcase: [],
  events: [],
  cta: {},
  contact: { email: "", phone: "", office: "" },
});

const COURSE_TYPES = ["", "Theory", "Studio", "Lab", "Practical", "Project", "Thesis"];

/* ---------------------------------------------------------------- bits */

function Field({ label, hint, children, className = "" }) {
  return (
    <label className={`flex flex-col gap-1 ${className}`}>
      <span className="text-[12px] font-semibold text-ink">{label}</span>
      {children}
      {hint && <span className="text-[11px] text-muted leading-snug">{hint}</span>}
    </label>
  );
}

function Panel({ title, hint, action, children }) {
  return (
    <section className="border border-line rounded-xl overflow-hidden bg-white">
      <div className="bg-[#f6f8f7] px-4 py-2.5 border-b border-line flex items-center justify-between gap-3">
        <div>
          <div className="text-[12.5px] font-bold text-ink">{title}</div>
          {hint && <div className="text-[11.5px] text-muted mt-0.5">{hint}</div>}
        </div>
        {action}
      </div>
      <div className="p-4 flex flex-col gap-3.5">{children}</div>
    </section>
  );
}

/** Generic list editor with labelled fields per row, plus reorder + remove. */
function Rows({ rows, fields, onChange, blank, addLabel = "+ Add", empty = "None yet." }) {
  const list = rows || [];
  const upd = (i, k, v) => onChange(list.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));
  const move = (i, d) => {
    const j = i + d;
    if (j < 0 || j >= list.length) return;
    const c = [...list];
    [c[i], c[j]] = [c[j], c[i]];
    onChange(c);
  };
  return (
    <div className="flex flex-col gap-3">
      {list.length === 0 && <div className="text-muted text-[13px]">{empty}</div>}
      {list.map((row, i) => (
        <div key={i} className="border border-line rounded-xl p-3.5 flex gap-3 items-start bg-[#fcfcfb]">
          <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-2.5">
            {fields.map(([key, label, opts = {}]) => (
              <Field key={key} label={label} hint={opts.hint} className={opts.wide ? "md:col-span-2" : ""}>
                {opts.type === "select" ? (
                  <select value={row[key] || ""} onChange={(e) => upd(i, key, e.target.value)} className={inputCls}>
                    {opts.options.map((o) => (
                      <option key={o} value={o}>{o || "—"}</option>
                    ))}
                  </select>
                ) : opts.type === "textarea" ? (
                  <textarea value={row[key] || ""} onChange={(e) => upd(i, key, e.target.value)} rows={2} placeholder={opts.ph} className={`${inputCls} resize-y`} />
                ) : (
                  <input value={row[key] || ""} onChange={(e) => upd(i, key, e.target.value)} placeholder={opts.ph} className={`${inputCls} ${opts.narrow ? "md:max-w-[140px]" : ""}`} />
                )}
              </Field>
            ))}
          </div>
          <div className="flex flex-col gap-1.5 pt-5">
            <button type="button" onClick={() => move(i, -1)} disabled={i === 0} title="Move up" className={btnIcon}>↑</button>
            <button type="button" onClick={() => move(i, 1)} disabled={i === list.length - 1} title="Move down" className={btnIcon}>↓</button>
            <button type="button" onClick={() => onChange(list.filter((_, idx) => idx !== i))} title="Remove" className={btnDanger}>🗑</button>
          </div>
        </div>
      ))}
      <div>
        <button type="button" onClick={() => onChange([...list, blank()])} className={btnGhost}>{addLabel}</button>
      </div>
    </div>
  );
}

/** Image upload control (shared media endpoint). */
function PhotoField({ value, onChange, label = "Photo", hint }) {
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append("image", file);
      const { url } = await adminPostForm("/admin/media", form);
      onChange(url);
    } catch {
      /* page-level error banner reports on save */
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex items-center gap-4">
      {mediaUrl(value) ? (
        <img src={mediaUrl(value)} alt="" className="w-20 h-20 rounded-2xl object-cover border border-line shrink-0" />
      ) : (
        <div className="w-20 h-20 rounded-2xl border border-dashed border-line bg-[#f3f4f2] flex items-center justify-center text-[11px] text-muted text-center leading-tight shrink-0 px-2">
          No {label.toLowerCase()} yet
        </div>
      )}
      <div className="flex flex-col gap-1.5">
        <label className="inline-flex w-fit items-center cursor-pointer">
          <input type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
          <span className={btnGhost}>{busy ? "Uploading…" : value ? `Replace ${label.toLowerCase()}` : `＋ Upload ${label.toLowerCase()}`}</span>
        </label>
        {value && (
          <button type="button" onClick={() => onChange(null)} className="text-[12px] text-[#c44] bg-transparent border-none cursor-pointer p-0 text-left">
            Remove {label.toLowerCase()}
          </button>
        )}
        {hint && <span className="text-[11px] text-muted">{hint}</span>}
      </div>
    </div>
  );
}

/**
 * Banner slides: photo + optional caption per row, plus the per-slide options
 * every slider on the site shares (show text, same text as previous slide,
 * animation, seconds). Saved as `slides[]` on the department.
 */
function SlideRows({ rows, onChange }) {
  const list = rows || [];
  const patch = (i, p) => onChange(list.map((r, idx) => (idx === i ? { ...r, ...p } : r)));
  const move = (i, d) => {
    const j = i + d;
    if (j < 0 || j >= list.length) return;
    const c = [...list];
    [c[i], c[j]] = [c[j], c[i]];
    onChange(c);
  };
  return (
    <div className="flex flex-col gap-3">
      {list.length === 0 && (
        <div className="text-muted text-[13px]">
          No banner photos yet — the banner shows the department colour on its own.
        </div>
      )}
      {list.map((row, i) => (
        <div key={i} className="border border-line rounded-xl p-3.5 flex gap-3 items-start bg-[#fcfcfb]">
          <div className="flex-1 flex flex-col gap-2.5">
            {/* Desktop + mobile photo uploads (shared picker); shown dimmed behind the title */}
            <SlideImages slide={row} variant="department" onChange={(p) => patch(i, p)} />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
              <Field label="Caption line (optional)" hint="Replaces the tagline while this photo is showing.">
                <input value={row.headline || ""} onChange={(e) => patch(i, { headline: e.target.value })} className={inputCls} disabled={!!row.textFromPrevious} />
              </Field>
              <Field label="Smaller line under it (optional)">
                <input value={row.sub || ""} onChange={(e) => patch(i, { sub: e.target.value })} className={inputCls} disabled={!!row.textFromPrevious} />
              </Field>
            </div>
            <SlideOptions
              slide={row}
              index={i}
              defaultDuration={6}
              onChange={(p) => patch(i, p)}
              onApplyAnimationToAll={(animation) =>
                onChange(list.map((r) => ({ ...r, animation: animation === "fade" ? undefined : animation })))
              }
            />
          </div>
          <div className="flex flex-col gap-1.5 pt-1">
            <button type="button" onClick={() => move(i, -1)} disabled={i === 0} title="Move up" className={btnIcon}>↑</button>
            <button type="button" onClick={() => move(i, 1)} disabled={i === list.length - 1} title="Move down" className={btnIcon}>↓</button>
            <button type="button" onClick={() => onChange(list.filter((_, idx) => idx !== i))} title="Remove" className={btnDanger}>🗑</button>
          </div>
        </div>
      ))}
      <div>
        <button type="button" onClick={() => onChange([...list, { image: null, mobileImage: null, headline: "", sub: "" }])} className={btnGhost}>
          + Add banner photo
        </button>
      </div>
    </div>
  );
}

/* --------------------------------------------------------- tab bodies */

function BasicsTab({ d, set }) {
  return (
    <>
      <Panel title="Identity" hint="Who this department is and where its page lives.">
        <div className="grid grid-cols-1 md:grid-cols-[140px_1fr_140px] gap-3">
          <Field label="Short code" hint="Chip on the homepage, e.g. FNS.">
            <input value={d.code} onChange={(e) => set("code", e.target.value.toUpperCase())} placeholder="FNS" className={inputCls} />
          </Field>
          <Field label="Department name">
            <input
              value={d.name}
              onChange={(e) => {
                const name = e.target.value;
                set("name", name);
                if (!d.slug || d.slug === slugify(d.name)) set("slug", slugify(name));
              }}
              placeholder="Food & Nutrition Science"
              className={inputCls}
            />
          </Field>
          <Field label="Colour">
            <input type="color" value={d.color || "#9B7200"} onChange={(e) => set("color", e.target.value)} className="w-full h-[38px] border border-line rounded-lg cursor-pointer bg-white" />
          </Field>
        </div>
        <Field label="Page address" hint="Filled in automatically from the name. Only lowercase letters, numbers and dashes.">
          <div className="flex items-center gap-2">
            <span className="text-[13px] text-muted whitespace-nowrap">/departments/</span>
            <input value={d.slug} onChange={(e) => set("slug", slugify(e.target.value))} className={inputCls} />
          </div>
        </Field>
      </Panel>

      <Panel title="Banner text" hint="The big coloured banner at the top of the page.">
        <Field label="Tagline" hint="One sentence under the title.">
          <input value={d.tagline} onChange={(e) => set("tagline", e.target.value)} className={inputCls} />
        </Field>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Small line above the title" hint="e.g. Honours Department · Khulna University">
            <input value={d.kicker} onChange={(e) => set("kicker", e.target.value)} className={inputCls} />
          </Field>
          <Field label="Footnote under the buttons (optional)">
            <input value={d.footnote || ""} onChange={(e) => set("footnote", e.target.value)} className={inputCls} />
          </Field>
        </div>
      </Panel>

      <Panel title="Banner photos (slider)" hint="Photos behind the banner. Add more than one to make it a slideshow; each photo has its own text and animation settings.">
        <SlideRows rows={d.slides} onChange={(v) => set("slides", v)} />
      </Panel>

      <Panel title="About the department">
        <Field label="Main paragraph" hint="Starts with a large drop-cap on the page.">
          <textarea value={d.about} onChange={(e) => set("about", e.target.value)} rows={4} className={`${inputCls} resize-y`} />
        </Field>
        <Field label="Second paragraph (optional)">
          <textarea value={d.aboutMore || ""} onChange={(e) => set("aboutMore", e.target.value)} rows={2} className={`${inputCls} resize-y`} />
        </Field>
      </Panel>

      <Panel title="Admissions" hint="Used by the Programmes section and the banner at the bottom of the page.">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Admissions status" hint="Shown as a green pill, e.g. “Admissions open for 2025–26”. Leave blank to hide.">
            <input value={d.admissionsStatus || ""} onChange={(e) => set("admissionsStatus", e.target.value)} className={inputCls} />
          </Field>
          <Field label="Apply button goes to" hint="Leave blank for the honours admissions page.">
            <input value={d.applyHref || ""} onChange={(e) => set("applyHref", e.target.value)} placeholder="/admissions/honors" className={inputCls} />
          </Field>
        </div>
      </Panel>
    </>
  );
}

function BannerTab({ d, set, setObj }) {
  const hl = d.highlight || {};
  return (
    <>
      <Panel title="Numbers strip" hint="The white card under the banner. Up to four numbers.">
        <Rows
          rows={d.stats}
          blank={() => ({ value: "", label: "", note: "" })}
          fields={[
            ["value", "Number", { ph: "120", narrow: true }],
            ["label", "Label", { ph: "Students" }],
            ["note", "Small caption", { ph: "Across all four years", wide: true }],
          ]}
          onChange={(v) => set("stats", v)}
          addLabel="+ Add number"
        />
      </Panel>

      <Panel title="Highlight card (optional)" hint="A featured item beside the banner, e.g. a prize-winning student project. Hidden while the title is blank.">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Card label"><input value={hl.label || ""} onChange={(e) => setObj("highlight", "label", e.target.value)} placeholder="Studio Showcase 2025" className={inputCls} /></Field>
          <Field label="Small line above the title"><input value={hl.eyebrow || ""} onChange={(e) => setObj("highlight", "eyebrow", e.target.value)} placeholder="Featured thesis work" className={inputCls} /></Field>
          <Field label="Title"><input value={hl.title || ""} onChange={(e) => setObj("highlight", "title", e.target.value)} className={inputCls} /></Field>
          <Field label="By"><input value={hl.by || ""} onChange={(e) => setObj("highlight", "by", e.target.value)} placeholder="By Student Name (Batch ’24)" className={inputCls} /></Field>
          <Field label="Three short tags" hint="Comma separated"><input value={(hl.tags || []).join(", ")} onChange={(e) => setObj("highlight", "tags", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} className={inputCls} /></Field>
          <Field label="Footer line"><input value={hl.footer || ""} onChange={(e) => setObj("highlight", "footer", e.target.value)} placeholder="Admissions open • 40 seats" className={inputCls} /></Field>
        </div>
        <Field label="Quote"><input value={hl.quote || ""} onChange={(e) => setObj("highlight", "quote", e.target.value)} className={inputCls} /></Field>
      </Panel>

      <Panel title="Three pillars" hint="Small cards beside the About text — what the department is known for.">
        <Rows
          rows={d.pillars}
          blank={() => ({ icon: "✦", title: "", desc: "" })}
          fields={[
            ["icon", "Icon (emoji)", { ph: "✂️", narrow: true }],
            ["title", "Title", { ph: "Pattern Drafting" }],
            ["desc", "One-line description", { wide: true }],
          ]}
          onChange={(v) => set("pillars", v)}
          addLabel="+ Add pillar"
        />
      </Panel>
    </>
  );
}

function ProgrammesTab({ d, set }) {
  return (
    <Panel title="Programmes offered" hint="Each becomes a large card. Most departments have one.">
      <Rows
        rows={d.programmes}
        blank={() => ({ name: "", level: "Undergraduate", duration: "4 years", credits: "", medium: "", seats: "", desc: "", eligibility: "", syllabusUrl: "" })}
        fields={[
          ["name", "Programme name", { ph: "BSc (Honours) in …", wide: true }],
          ["level", "Level", { ph: "Undergraduate" }],
          ["duration", "Duration", { ph: "4 years" }],
          ["credits", "Total credits", { ph: "128 credits" }],
          ["medium", "Medium of instruction", { ph: "Bangla / English" }],
          ["seats", "Seats", { ph: "40 seats" }],
          ["syllabusUrl", "Syllabus PDF link (optional)", { ph: "https://…" }],
          ["desc", "Short description", { type: "textarea", wide: true }],
          ["eligibility", "Who can apply", { type: "textarea", wide: true }],
        ]}
        onChange={(v) => set("programmes", v)}
        addLabel="+ Add programme"
      />
    </Panel>
  );
}

function CurriculumTab({ d, set }) {
  const years = d.curriculum || [];
  const updYear = (i, patch) => set("curriculum", years.map((y, idx) => (idx === i ? { ...y, ...patch } : y)));
  const moveYear = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= years.length) return;
    const c = [...years];
    [c[i], c[j]] = [c[j], c[i]];
    set("curriculum", c);
  };
  return (
    <>
      <Panel
        title="Curriculum by year"
        hint="One block per year. Each course is a row — no special formatting needed."
        action={
          <button type="button" onClick={() => set("curriculum", [...years, { year: `${years.length + 1}${["st", "nd", "rd"][years.length] || "th"} Year`, title: "", focus: "", courses: [] }])} className={btnPrimary}>
            + Add year
          </button>
        }
      >
        {years.length === 0 && (
          <div className="text-muted text-[13px]">No years yet. Until you add one, the simple course list below is shown instead.</div>
        )}
        {years.map((yr, yi) => (
          <div key={yi} className="border-[1.5px] border-line rounded-xl overflow-hidden">
            <div className="bg-[#f6f8f7] px-3.5 py-2.5 flex items-center gap-2">
              <span className="w-7 h-7 rounded-full bg-green text-white text-[12px] font-bold flex items-center justify-center shrink-0">{yi + 1}</span>
              <input value={yr.year || ""} onChange={(e) => updYear(yi, { year: e.target.value })} placeholder="1st Year" className={`${inputCls} md:max-w-[130px]`} />
              <input value={yr.title || ""} onChange={(e) => updYear(yi, { title: e.target.value })} placeholder="Theme, e.g. Foundations" className={inputCls} />
              <input value={yr.focus || ""} onChange={(e) => updYear(yi, { focus: e.target.value })} placeholder="Focus line (optional)" className={inputCls} />
              <button type="button" onClick={() => moveYear(yi, -1)} disabled={yi === 0} className={btnIcon}>↑</button>
              <button type="button" onClick={() => moveYear(yi, 1)} disabled={yi === years.length - 1} className={btnIcon}>↓</button>
              <button type="button" onClick={() => set("curriculum", years.filter((_, i) => i !== yi))} title="Remove year" className={btnDanger}>🗑</button>
            </div>
            <div className="p-3.5">
              <Rows
                rows={yr.courses}
                blank={() => ({ code: "", name: "", credits: "", type: "", desc: "" })}
                fields={[
                  ["code", "Course code", { ph: "FNS-101", narrow: true }],
                  ["name", "Course name", { ph: "Introduction to Nutrition" }],
                  ["credits", "Credits", { ph: "4", narrow: true }],
                  ["type", "Type", { type: "select", options: COURSE_TYPES }],
                  ["desc", "What it covers (optional)", { wide: true }],
                ]}
                onChange={(v) => updYear(yi, { courses: v })}
                addLabel="+ Add course"
                empty="No courses in this year yet."
              />
            </div>
          </div>
        ))}
      </Panel>

      <Panel title="Simple course list (fallback)" hint="Only used when no years are set above. One subject per line.">
        <textarea
          value={(d.courses || []).join("\n")}
          onChange={(e) => set("courses", e.target.value.split("\n").map((c) => c.trim()).filter(Boolean))}
          rows={5}
          className={`${inputCls} resize-y`}
        />
      </Panel>
    </>
  );
}

function PeopleTab({ d, set, setObj }) {
  const head = d.head || {};
  return (
    <>
      <Panel title="Head of the department" hint="Shown as a highlighted card with photo, name and a short quote.">
        <PhotoField value={head.photo} onChange={(url) => setObj("head", "photo", url)} label="Photo" hint="Square portrait works best. A placeholder shows until one is uploaded." />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Field label="Name"><input value={head.name || ""} onChange={(e) => setObj("head", "name", e.target.value)} className={inputCls} /></Field>
          <Field label="Role"><input value={head.role || ""} onChange={(e) => setObj("head", "role", e.target.value)} placeholder="Head of the Department" className={inputCls} /></Field>
          <Field label="Qualification"><input value={head.qualification || ""} onChange={(e) => setObj("head", "qualification", e.target.value)} placeholder="MSc, Dhaka University" className={inputCls} /></Field>
        </div>
        <Field label="Message" hint="A short quote shown under the name.">
          <textarea value={head.message || ""} onChange={(e) => setObj("head", "message", e.target.value)} rows={3} className={`${inputCls} resize-y`} />
        </Field>
        <Field label="Short bio (optional)">
          <textarea value={head.bio || ""} onChange={(e) => setObj("head", "bio", e.target.value)} rows={2} className={`${inputCls} resize-y`} />
        </Field>
      </Panel>

      <Panel title="Faculty members" hint="Teachers shown as cards. Rows without a name are hidden on the site.">
        <Rows
          rows={d.faculty}
          blank={() => ({ name: "", role: "", specialization: "", qualification: "" })}
          fields={[
            ["name", "Name"],
            ["role", "Role", { ph: "Assistant Professor" }],
            ["specialization", "Specialization", { ph: "Textile science, natural dyes", wide: true }],
            ["qualification", "Qualification / experience", { ph: "MSc, Dhaka University • 10+ yrs", wide: true }],
          ]}
          onChange={(v) => set("faculty", v)}
          addLabel="+ Add teacher"
        />
      </Panel>
    </>
  );
}

function FacilitiesTab({ d, set }) {
  return (
    <Panel title="Facilities & labs" hint="Rooms, labs and studios. Equipment shows as small chips.">
      <Rows
        rows={(d.facilities || []).map((f) => ({ ...f, equipmentText: f.equipmentText ?? (f.equipment || []).join(", ") }))}
        blank={() => ({ icon: "🔬", name: "", location: "", desc: "", equipment: [], equipmentText: "" })}
        fields={[
          ["icon", "Icon (emoji)", { ph: "🔬", narrow: true }],
          ["name", "Name", { ph: "Nutrition Laboratory" }],
          ["location", "Location", { ph: "Lab 01 • Ground floor" }],
          ["equipmentText", "Equipment", { ph: "Comma separated" }],
          ["desc", "Short description", { type: "textarea", wide: true }],
        ]}
        onChange={(v) =>
          set(
            "facilities",
            v.map((f) => ({
              ...f,
              equipment: String(f.equipmentText || "").split(",").map((s) => s.trim()).filter(Boolean),
            }))
          )
        }
        addLabel="+ Add facility"
      />
    </Panel>
  );
}

function MoreTab({ d, set, setObj }) {
  const cta = d.cta || {};
  const c = d.contact || {};
  return (
    <>
      <Panel title="Student showcase" hint="Selected student work shown as cards. Hidden while empty.">
        <Rows
          rows={d.showcase}
          blank={() => ({ eyebrow: "", title: "", by: "", tag: "", desc: "" })}
          fields={[
            ["title", "Project title"],
            ["by", "By", { ph: "Designer: Student Name (’24)" }],
            ["eyebrow", "Small line", { ph: "Lookbook No. 01" }],
            ["tag", "Short tag", { ph: "Zero-waste cut" }],
            ["desc", "Short description", { type: "textarea", wide: true }],
          ]}
          onChange={(v) => set("showcase", v)}
          addLabel="+ Add item"
        />
      </Panel>

      <Panel title="Events" hint="Listed in the noticeboard area.">
        <Rows
          rows={d.events}
          blank={() => ({ date: "", title: "", venue: "", desc: "" })}
          fields={[
            ["date", "Date", { ph: "15 Jan", narrow: true }],
            ["title", "Event"],
            ["venue", "Venue", { wide: true }],
          ]}
          onChange={(v) => set("events", v)}
          addLabel="+ Add event"
        />
      </Panel>

      <Panel title="Department office" hint="Contact box. Leave everything blank to hide it.">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Field label="Room / office"><input value={c.office || ""} onChange={(e) => setObj("contact", "office", e.target.value)} className={inputCls} /></Field>
          <Field label="Phone"><input value={c.phone || ""} onChange={(e) => setObj("contact", "phone", e.target.value)} className={inputCls} /></Field>
          <Field label="Email"><input value={c.email || ""} onChange={(e) => setObj("contact", "email", e.target.value)} className={inputCls} /></Field>
        </div>
      </Panel>

      <Panel title="Bottom call-to-action" hint="The dark banner at the end of the page. Leave blank to use the default wording.">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Small line"><input value={cta.eyebrow || ""} onChange={(e) => setObj("cta", "eyebrow", e.target.value)} placeholder="Admissions 2025–26" className={inputCls} /></Field>
          <Field label="Heading"><input value={cta.title || ""} onChange={(e) => setObj("cta", "title", e.target.value)} className={inputCls} /></Field>
        </div>
        <Field label="Text"><textarea value={cta.desc || ""} onChange={(e) => setObj("cta", "desc", e.target.value)} rows={2} className={`${inputCls} resize-y`} /></Field>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <Field label="Button 1 text"><input value={cta.primaryLabel || ""} onChange={(e) => setObj("cta", "primaryLabel", e.target.value)} className={inputCls} /></Field>
          <Field label="Button 1 link"><input value={cta.primaryHref || ""} onChange={(e) => setObj("cta", "primaryHref", e.target.value)} className={inputCls} /></Field>
          <Field label="Button 2 text"><input value={cta.secondaryLabel || ""} onChange={(e) => setObj("cta", "secondaryLabel", e.target.value)} className={inputCls} /></Field>
          <Field label="Button 2 link"><input value={cta.secondaryHref || ""} onChange={(e) => setObj("cta", "secondaryHref", e.target.value)} className={inputCls} /></Field>
        </div>
      </Panel>
    </>
  );
}

const TABS = [
  ["basics", "Basics", BasicsTab],
  ["banner", "Banner & numbers", BannerTab],
  ["programmes", "Programmes", ProgrammesTab],
  ["curriculum", "Curriculum", CurriculumTab],
  ["people", "People", PeopleTab],
  ["facilities", "Facilities", FacilitiesTab],
  ["more", "More", MoreTab],
];

/* ------------------------------------------------------ one department */

function DepartmentEditor({ dept, onChange, onRemove, onMove, first, last }) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("basics");
  const set = (k, v) => onChange({ ...dept, [k]: v });
  const setObj = (obj, k, v) => onChange({ ...dept, [obj]: { ...(dept[obj] || {}), [k]: v } });
  const Body = TABS.find(([k]) => k === tab)?.[2] || BasicsTab;

  const counts = [
    dept.programmes?.length ? `${dept.programmes.length} programme${dept.programmes.length === 1 ? "" : "s"}` : null,
    dept.curriculum?.length ? `${dept.curriculum.length} years` : null,
    (dept.faculty || []).filter((f) => f.name).length ? `${(dept.faculty || []).filter((f) => f.name).length} teachers` : null,
  ].filter(Boolean);

  return (
    <div className="border-[1.5px] rounded-xl bg-white" style={{ borderColor: dept.color }}>
      <div className="flex items-center gap-3 p-3.5 flex-wrap">
        <span className="w-12 h-10 rounded-lg flex items-center justify-center font-serif font-bold text-[12px] shrink-0" style={{ background: `${dept.color}18`, color: dept.color }}>
          {dept.code || "—"}
        </span>
        <div className="flex-1 min-w-[200px]">
          <div className="text-[14px] font-semibold text-ink truncate">{dept.name || "Untitled department"}</div>
          <div className="text-[11.5px] text-muted">
            /departments/{dept.slug || "…"}{counts.length ? ` · ${counts.join(" · ")}` : ""}
          </div>
        </div>
        {dept.slug && (
          <a href={`/departments/${dept.slug}`} target="_blank" rel="noreferrer" className={`${btnGhost} no-underline`}>
            View page ↗
          </a>
        )}
        <button type="button" onClick={() => setOpen((o) => !o)} className={btnPrimary}>{open ? "Close" : "Edit"}</button>
        <div className="flex gap-1">
          <button type="button" onClick={() => onMove(-1)} disabled={first} title="Move up" className={btnIcon}>↑</button>
          <button type="button" onClick={() => onMove(1)} disabled={last} title="Move down" className={btnIcon}>↓</button>
          <button type="button" onClick={onRemove} title="Remove department" className={btnDanger}>🗑</button>
        </div>
      </div>

      {open && (
        <div className="border-t border-line bg-[#fafbfa]">
          <div className="flex gap-1 px-3.5 pt-3 overflow-x-auto border-b border-line">
            {TABS.map(([key, label]) => (
              <button
                key={key}
                type="button"
                onClick={() => setTab(key)}
                className="whitespace-nowrap px-3.5 py-2 text-[12.5px] font-semibold rounded-t-lg border-b-2 -mb-px bg-transparent cursor-pointer"
                style={{
                  color: tab === key ? "#005C40" : "#5c5c5c",
                  borderBottomColor: tab === key ? "#005C40" : "transparent",
                }}
              >
                {label}
              </button>
            ))}
          </div>
          <div className="p-4 flex flex-col gap-4">
            <Body d={dept} set={set} setObj={setObj} />
          </div>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------- page */

export default function DepartmentsPage() {
  const [items, setItems] = useState([]);
  const [saved, setSaved] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [error, setError] = useState("");
  const [status, setStatus] = useState("");
  const [saving, setSaving] = useState(false);

  const dirty = JSON.stringify(items) !== saved;

  const load = useCallback(async () => {
    setLoading(true);
    setLoadFailed(false);
    setError("");
    try {
      const d = (await adminGet("/admin/content/departments")) || {};
      const list = d.items || [];
      setItems(list);
      setSaved(JSON.stringify(list));
    } catch (e) {
      setLoadFailed(true);
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Warn before leaving with unsaved edits.
  useEffect(() => {
    if (!dirty) return;
    const h = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [dirty]);

  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= items.length) return;
    const c = [...items];
    [c[i], c[j]] = [c[j], c[i]];
    setItems(c);
  };

  const save = async () => {
    if (loadFailed) {
      setError("Content didn't load, so saving now would erase it. Reload the page first.");
      return;
    }
    const missing = items.filter((d) => !(d.slug || "").trim() || !(d.name || "").trim());
    if (missing.length) {
      setError("Every department needs a name and a page address (Basics tab).");
      return;
    }
    setSaving(true);
    setStatus("");
    setError("");
    try {
      // equipmentText is an editor convenience only
      const payload = {
        items: items.map((d) => ({
          ...d,
          facilities: (d.facilities || []).map(({ equipmentText, ...f }) => f),
        })),
      };
      const res = await adminPut("/admin/content/departments", payload);
      const list = res?.items || payload.items;
      setItems(list);
      setSaved(JSON.stringify(list));
      setStatus("Saved.");
      setTimeout(() => setStatus(""), 2500);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="px-1 py-8 text-muted text-sm">Loading…</div>;

  return (
    <div className="animate-fadeIn pb-24">
      {loadFailed && <LoadFailedBanner onRetry={load} />}
      {error && <div className="text-[13px] text-red bg-red/[0.08] rounded-lg px-4 py-2.5 mb-4">{error}</div>}

      <AdminCard className="mb-[18px]">
        <AdminCardHead
          title={`Departments (${items.length})`}
          action={
            <button type="button" onClick={() => setItems([...items, blankDept()])} className={btnPrimary}>
              + Add department
            </button>
          }
        />
        <div className="p-[22px] flex flex-col gap-3">
          <div className="text-[12.5px] text-muted bg-[#f6f8f7] border border-line rounded-lg px-3.5 py-2.5 leading-relaxed">
            Each department gets its own page. Click <strong>Edit</strong>, work through the tabs, then press
            <strong> Save changes</strong> at the bottom — the bar turns amber while you have unsaved edits.
            The <strong>short code</strong> (e.g. FNS) is what links the chip on the homepage to the page.
          </div>
          {items.length === 0 && <div className="text-muted text-sm">No departments yet.</div>}
          {items.map((d, i) => (
            <DepartmentEditor
              key={i}
              dept={d}
              first={i === 0}
              last={i === items.length - 1}
              onMove={(dir) => move(i, dir)}
              onChange={(next) => setItems(items.map((x, idx) => (idx === i ? next : x)))}
              onRemove={() => {
                if (window.confirm(`Remove “${d.name || "this department"}”? Its page will disappear after saving.`)) {
                  setItems(items.filter((_, idx) => idx !== i));
                }
              }}
            />
          ))}
        </div>
      </AdminCard>

      {/* Pinned save bar */}
      <div
        className="fixed bottom-0 left-0 right-0 z-[50] border-t px-6 py-3 flex items-center justify-between gap-4 backdrop-blur"
        style={{
          background: dirty ? "rgba(255,247,230,0.95)" : "rgba(255,255,255,0.95)",
          borderColor: dirty ? "#f0d9a6" : "#e2ddd6",
        }}
      >
        <span className="text-[13px] font-semibold" style={{ color: dirty ? "#9B7200" : "#5c5c5c" }}>
          {dirty ? "● You have unsaved changes" : status || "All changes saved"}
        </span>
        <div className="flex items-center gap-2">
          {dirty && (
            <button type="button" onClick={() => setItems(JSON.parse(saved || "[]"))} className={btnGhost}>
              Discard
            </button>
          )}
          <button type="button" onClick={save} disabled={saving || !dirty} className={`${btnPrimary} px-6 py-2.5 text-[13px]`}>
            {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>
    </div>
  );
}
